import React, { useState } from 'react';

const UploadOptimizer = () => {
    const [topic, setTopic] = useState('');
    const [timezone, setTimezone] = useState('UTC');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');

    const handleOptimize = async (e) => {
        e.preventDefault();
        if (!topic.trim()) return;

        setLoading(true);
        setError('');
        setResult(null);

        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/analysis/upload-optimizer', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ topic: topic.trim(), timezone })
            });

            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(data.message || 'Failed to generate upload schedule.');
            }
            setResult(data.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const getScoreColor = (score) => {
        if (score >= 80) return 'rgba(52, 211, 153, 0.85)';
        if (score >= 60) return 'rgba(99, 102, 241, 0.75)';
        if (score >= 40) return 'rgba(251, 191, 36, 0.65)';
        return 'rgba(30, 41, 59, 0.5)';
    };

    return (
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem 1rem', color: '#f8fafc' }}>
            <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
                <div style={{ 
                    display: 'inline-block', 
                    padding: '0.4rem 1rem', 
                    background: 'rgba(52, 211, 153, 0.15)', 
                    border: '1px solid rgba(52, 211, 153, 0.3)', 
                    borderRadius: '20px', 
                    fontSize: '0.85rem', 
                    color: '#34d399',
                    marginBottom: '0.8rem',
                    fontWeight: 600
                }}>
                    📅 Phase 2 Differentiation Feature (15 Credits)
                </div>
                <h1 style={{ 
                    fontSize: '2.5rem', 
                    fontWeight: 800, 
                    background: 'linear-gradient(135deg, #34d399, #3b82f6, #6366f1)', 
                    WebkitBackgroundClip: 'text', 
                    WebkitTextFillColor: 'transparent',
                    marginBottom: '0.5rem'
                }}>
                    Content Calendar & Upload Schedule Optimizer
                </h1>
                <p style={{ color: '#94a3b8', fontSize: '1.05rem', maxWidth: '650px', margin: '0 auto' }}>
                    Correlate publication timestamps with viewer activity across a 24x7 heatmap to capture maximum initial 3-hour video velocity.
                </p>
            </div>

            <form onSubmit={handleOptimize} style={{ 
                background: 'rgba(15, 23, 42, 0.75)', 
                backdropFilter: 'blur(16px)', 
                border: '1px solid rgba(255, 255, 255, 0.1)', 
                borderRadius: '16px', 
                padding: '1.5rem', 
                marginBottom: '2rem',
                display: 'flex',
                gap: '1rem',
                flexWrap: 'wrap'
            }}>
                <input
                    type="text"
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    placeholder="Enter video category/niche (e.g. Software Engineering, Tech Reviews, Gaming)..."
                    style={{
                        flex: 2,
                        minWidth: '260px',
                        background: 'rgba(30, 41, 59, 0.8)',
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                        borderRadius: '10px',
                        padding: '0.85rem 1.2rem',
                        color: '#fff',
                        fontSize: '1rem',
                        outline: 'none'
                    }}
                />
                <select
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    style={{
                        flex: 1,
                        minWidth: '150px',
                        background: 'rgba(30, 41, 59, 0.8)',
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                        borderRadius: '10px',
                        padding: '0.85rem 1rem',
                        color: '#fff',
                        fontSize: '1rem'
                    }}
                >
                    <option value="UTC">UTC (Universal)</option>
                    <option value="IST">IST (India - UTC+5:30)</option>
                    <option value="EST">EST (US East)</option>
                    <option value="PST">PST (US West)</option>
                </select>
                <button
                    type="submit"
                    disabled={loading}
                    style={{
                        background: 'linear-gradient(135deg, #10b981, #059669)',
                        border: 'none',
                        borderRadius: '10px',
                        padding: '0.85rem 2rem',
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: '1rem',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        boxShadow: '0 4px 14px rgba(16, 185, 129, 0.4)'
                    }}
                >
                    {loading ? 'Calculating Heatmap...' : 'Optimize Schedule (15 Credits)'}
                </button>
            </form>

            {error && (
                <div style={{ background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.3)', color: '#fca5a5', padding: '1rem 1.5rem', borderRadius: '12px', marginBottom: '2rem' }}>
                    ❌ {error}
                </div>
            )}

            {result && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                    {/* Recommended Slots Cards */}
                    <div>
                        <h2 style={{ fontSize: '1.4rem', fontWeight: 700, marginBottom: '1rem', color: '#f1f5f9' }}>
                            🏆 Top 3 Recommended Upload Slots
                        </h2>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.2rem' }}>
                            {result.recommendedSlots.map((slot) => (
                                <div key={slot.rank} style={{
                                    background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.8), rgba(15, 23, 42, 0.9))',
                                    border: slot.rank === 1 ? '1px solid rgba(52, 211, 153, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
                                    borderRadius: '16px',
                                    padding: '1.5rem',
                                    position: 'relative'
                                }}>
                                    <span style={{
                                        position: 'absolute',
                                        top: '1rem',
                                        right: '1rem',
                                        fontSize: '0.75rem',
                                        fontWeight: 800,
                                        padding: '0.3rem 0.7rem',
                                        borderRadius: '12px',
                                        background: slot.rank === 1 ? '#10b981' : '#6366f1',
                                        color: '#fff'
                                    }}>
                                        #{slot.rank} SLOT
                                    </span>
                                    <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#f8fafc', marginBottom: '0.3rem' }}>
                                        {slot.day}
                                    </div>
                                    <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#38bdf8', marginBottom: '0.8rem' }}>
                                        {slot.time} ({result.timezone})
                                    </div>
                                    <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
                                        {slot.recommendationReason}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* 24x7 Heatmap Grid */}
                    <div style={{
                        background: 'rgba(15, 23, 42, 0.8)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '16px',
                        padding: '1.5rem',
                        overflowX: 'auto'
                    }}>
                        <h2 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: '1rem', color: '#f1f5f9' }}>
                            📊 24-Hour x 7-Day Audience Activity Heatmap
                        </h2>
                        <div style={{ display: 'grid', gridTemplateColumns: '80px repeat(24, 1fr)', gap: '4px', minWidth: '700px' }}>
                            <div></div>
                            {Array.from({ length: 24 }).map((_, h) => (
                                <div key={h} style={{ fontSize: '0.7rem', textAlign: 'center', color: '#64748b' }}>
                                    {h}h
                                </div>
                            ))}

                            {result.heatmapMatrix.map((dayRow) => (
                                <React.Fragment key={dayRow.day}>
                                    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#cbd5e1', alignSelf: 'center' }}>
                                        {dayRow.day.slice(0, 3)}
                                    </div>
                                    {dayRow.hours.map((h) => (
                                        <div
                                            key={h.hour}
                                            title={`${dayRow.day} ${h.hour}:00 - Activity Score: ${h.score}/100`}
                                            style={{
                                                height: '28px',
                                                borderRadius: '4px',
                                                background: getScoreColor(h.score),
                                                transition: 'transform 0.15s ease',
                                                cursor: 'pointer'
                                            }}
                                        />
                                    ))}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UploadOptimizer;
